import { useAppSelector } from "../../../../../store/hooks";
import { updateStructureComponent } from "../../../../../store/reducers/builderReducers/structure/structureActions";
import { useEffect, useState, useRef, useCallback } from "react";
import React from "react";
import { TextEditorSelectStyle } from "../SingleProductMenu/SingleProductMenu";
import Select, { StylesConfig } from "react-select";
import { CheckoutComponent } from "../../../../_default/interfaces/base";
import { fetchApiGetUuid } from "../../../../../api/api";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CheckoutPageUrlInput from "./CheckoutPageUrlInput";

interface Props {
  id: number;
  triggerResize: () => void;
}

interface DeliveryMethodOption {
  uuid: string | null;
  name: string;
}

const FIXED_NONE_OPTION: DeliveryMethodOption = {
  uuid: null,
  name: "None",
};

function parseDeliveryMethodSearchResponse(response: unknown): DeliveryMethodOption[] {
  let items: unknown[] = [];
  if (Array.isArray(response)) {
    items = response;
  } else if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    Array.isArray((response as { data: unknown }).data)
  ) {
    items = (response as { data: unknown[] }).data;
  }

  return items
    .filter((entry) => entry && typeof entry === "object")
    .map((entry) => {
      const method = entry as {
        uuid?: string;
        name?: string;
        translations?: Record<string, { name?: string }>;
      };
      const name =
        method.name ??
        method.translations?.et?.name ??
        method.translations?.en?.name ??
        method.uuid ??
        "";
      if (typeof method.uuid !== "string" || method.uuid.length === 0) {
        return null;
      }
      return { uuid: method.uuid, name };
    })
    .filter(
      (entry): entry is { uuid: string; name: string } => entry !== null
    );
}

const selectStyles: StylesConfig<{ label: string; value: string }, false> = {
  ...TextEditorSelectStyle,
  container: (styles, data) => ({
    ...TextEditorSelectStyle.container!(styles, data),
    width: "fit-content",
  }),
  singleValue: (styles, data) => ({
    ...TextEditorSelectStyle.singleValue!(styles, data),
    width: "fit-content",
    padding: "0px",
  }),
};

export default function CheckoutMenu({ id, triggerResize }: Props) {
  const component = useAppSelector(
    (state) => state.builder.structure.components[id]
  )! as CheckoutComponent;

  const displays = [
    { label: "Hidden", value: "hidden" },
    { label: "Visible", value: "visible" },
  ];

  const requiredOptions = [
    { label: "Optional", value: "optional" },
    { label: "Required", value: "required" },
  ];

  const layouts = [
    { label: "Products 7/12", value: "default" },
    { label: "Products 12/12", value: "horizontal" },
  ];

  const deliverySources = [
    { label: "From order products", value: "from_order_products" },
    { label: "Fixed", value: "fixed" },
  ];

  const [fixedMethod, setFixedMethod] = useState<DeliveryMethodOption | null>(() => {
    if (
      component.attributes.delivery_method_source === "fixed" &&
      component.attributes.fixed_delivery_method_uuid === null
    ) {
      return {
        uuid: null,
        name: component.attributes.fixed_delivery_method_name ?? FIXED_NONE_OPTION.name,
      };
    }
    if (component.attributes.fixed_delivery_method_uuid) {
      return {
        uuid: component.attributes.fixed_delivery_method_uuid,
        name: component.attributes.fixed_delivery_method_name ?? "",
      };
    }
    return null;
  });
  const [searchQuery, setSearchQuery] = useState(
    component.attributes.fixed_delivery_method_name ?? ""
  );
  const [filteredMethods, setFilteredMethods] = useState<DeliveryMethodOption[]>(
    []
  );
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMenuClicked, setIsMenuClicked] = useState(false);
  const [isEditingFixed, setIsEditingFixed] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  const deliveryMethodSource =
    component.attributes.delivery_method_source ?? "from_order_products";
  const showFixedPicker =
    deliveryMethodSource === "fixed" || isEditingFixed;
  const sourceSelectValue = isEditingFixed ? "fixed" : deliveryMethodSource;

  const searchDeliveryMethods = useCallback(async (query: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchApiGetUuid<unknown>(
        `/delivery-methods?name=${encodeURIComponent(query)}`
      );
      setFilteredMethods(parseDeliveryMethodSearchResponse(response));
      setLoading(false);
    } catch (err) {
      console.error("Error searching delivery methods:", err);
      setError("Failed to load delivery methods");
      setFilteredMethods([]);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (component.attributes.fixed_delivery_method_uuid && fixedMethod?.name) {
      setSearchQuery(fixedMethod.name);
    }
    triggerResize();
  }, []);

  useEffect(() => {
    const uuid = component.attributes.fixed_delivery_method_uuid;
    const name = component.attributes.fixed_delivery_method_name ?? "";
    if (uuid === null) {
      if (component.attributes.delivery_method_source === "fixed") {
        const next = {
          uuid: null,
          name: name || FIXED_NONE_OPTION.name,
        };
        setFixedMethod(next);
        setSearchQuery(next.name);
        setIsEditingFixed(false);
      } else if (!isEditingFixed) {
        setFixedMethod(null);
        setSearchQuery("");
      }
      return;
    }
    if (!uuid) {
      if (!isEditingFixed) {
        setFixedMethod(null);
        setSearchQuery("");
      }
      return;
    }
    const next = { uuid, name };
    setFixedMethod(next);
    setSearchQuery(next.name);
    setIsEditingFixed(false);
  }, [
    component.attributes.fixed_delivery_method_uuid,
    component.attributes.fixed_delivery_method_name,
    isEditingFixed,
  ]);

  const revertPendingFixedSource = useCallback(() => {
    setIsEditingFixed(false);
    setFixedMethod(null);
    setSearchQuery("");
    setIsSearching(false);
    setFilteredMethods([]);
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      searchDeliveryMethods(value);
    }, 300);
  };

  const handleSearchFocus = () => {
    setIsSearching(true);
    if (!fixedMethod) {
      searchDeliveryMethods(searchQuery);
    }
    triggerResize();
  };

  const handleSearchBlur = () => {
    if (!isMenuClicked) {
      setTimeout(() => {
        setIsSearching(false);
        if (
          isEditingFixed &&
          !component.attributes.fixed_delivery_method_uuid &&
          !fixedMethod
        ) {
          revertPendingFixedSource();
        }
        triggerResize();
      }, 200);
    }
    setIsMenuClicked(false);
  };

  const handleSelectMethod = (method: DeliveryMethodOption) => {
    setFixedMethod(method);
    setSearchQuery(method.name);
    setIsSearching(false);
    setIsEditingFixed(false);
    updateStructureComponent(
      id,
      "attributes.delivery_method_source",
      "fixed"
    );
    updateStructureComponent(
      id,
      "attributes.fixed_delivery_method_uuid",
      method.uuid
    );
    updateStructureComponent(
      id,
      "attributes.fixed_delivery_method_name",
      method.name
    );
    triggerResize();
  };

  const handleClearMethod = (e: React.MouseEvent) => {
    e.stopPropagation();
    revertPendingFixedSource();
    updateStructureComponent(
      id,
      "attributes.delivery_method_source",
      "from_order_products"
    );
    updateStructureComponent(
      id,
      "attributes.fixed_delivery_method_uuid",
      null
    );
    updateStructureComponent(
      id,
      "attributes.fixed_delivery_method_name",
      null
    );
    triggerResize();
  };

  const handleSourceChange = (value: string) => {
    if (value === "fixed") {
      setIsEditingFixed(true);
      if (
        component.attributes.delivery_method_source === "fixed" &&
        component.attributes.fixed_delivery_method_uuid === null
      ) {
        setFixedMethod(FIXED_NONE_OPTION);
        setSearchQuery(FIXED_NONE_OPTION.name);
      } else if (component.attributes.fixed_delivery_method_uuid) {
        setFixedMethod({
          uuid: component.attributes.fixed_delivery_method_uuid,
          name: component.attributes.fixed_delivery_method_name ?? "",
        });
        setSearchQuery(component.attributes.fixed_delivery_method_name ?? "");
      } else {
        setFixedMethod(null);
        setSearchQuery("");
      }
      window.setTimeout(() => searchRef.current?.focus(), 0);
      triggerResize();
      return;
    }

    setIsEditingFixed(false);
    setFixedMethod(null);
    setSearchQuery("");
    updateStructureComponent(
      id,
      "attributes.delivery_method_source",
      "from_order_products"
    );
    updateStructureComponent(
      id,
      "attributes.fixed_delivery_method_uuid",
      null
    );
    updateStructureComponent(
      id,
      "attributes.fixed_delivery_method_name",
      null
    );
    triggerResize();
  };

  return (
    <div className="d-flex flex-column">
      <div className="d-flex flex-wrap align-items-center">
        <div style={{ marginRight: "5px" }}>Additional information:</div>

        <div className="border-menu_input" style={{ padding: "0px" }}>
          <Select
            menuPosition="fixed"
            className="b-mx-1 "
            isSearchable={false}
            styles={selectStyles}
            noOptionsMessage={() => ""}
            value={
              component.attributes.additional_info
                ? displays.find(
                    (x) => x.value === component.attributes.additional_info
                  )
                : displays[0]
            }
            onChange={(val) => {
              if (!val?.value) return;
              updateStructureComponent(
                id,
                "attributes.additional_info",
                val.value
              );
            }}
            options={displays}
            placeholder=""
          />
        </div>

        <div style={{ marginRight: "5px", marginLeft: "5px" }}>Layout:</div>

        <div className="border-menu_input" style={{ padding: "0px" }}>
          <Select
            menuPosition="fixed"
            className="b-mx-1 "
            isSearchable={false}
            styles={selectStyles}
            noOptionsMessage={() => ""}
            value={
              component.attributes.layout
                ? layouts.find((x) => x.value === component.attributes.layout)
                : layouts[0]
            }
            onChange={(val) => {
              if (!val?.value) return;
              updateStructureComponent(id, "attributes.layout", val.value);
            }}
            options={layouts}
            placeholder=""
          />
        </div>
      </div>

      <div
        className="d-flex flex-wrap align-items-center"
        style={{ marginTop: "3px" }}
      >
        <div style={{ marginRight: "5px" }}>B2B Contact Person:</div>

        <div className="border-menu_input" style={{ padding: "0px" }}>
          <Select
            menuPosition="fixed"
            className="b-mx-1"
            isSearchable={false}
            styles={selectStyles}
            noOptionsMessage={() => ""}
            value={
              component.attributes.b2b_contact_person
                ? displays.find(
                    (x) => x.value === component.attributes.b2b_contact_person
                  )
                : displays[0]
            }
            onChange={(val) => {
              if (!val?.value) return;
              updateStructureComponent(
                id,
                "attributes.b2b_contact_person",
                val.value
              );
            }}
            options={displays}
            placeholder=""
          />
        </div>

        <div className="border-menu_input b-mx-1" style={{ padding: "0px" }}>
          <Select
            menuPosition="fixed"
            className="b-mx-1"
            isSearchable={false}
            styles={selectStyles}
            noOptionsMessage={() => ""}
            value={
              component.attributes.b2b_contact_person_required
                ? requiredOptions.find(
                    (x) =>
                      x.value ===
                      component.attributes.b2b_contact_person_required
                  )
                : requiredOptions[1]
            }
            onChange={(val) => {
              if (!val?.value) return;
              updateStructureComponent(
                id,
                "attributes.b2b_contact_person_required",
                val.value
              );
            }}
            options={requiredOptions}
            placeholder=""
          />
        </div>
      </div>

      <div
        className="d-flex flex-wrap align-items-center"
        style={{ marginTop: "3px" }}
      >
        <div style={{ marginRight: "5px" }}>Delivery method:</div>

        <div className="border-menu_input" style={{ padding: "0px" }}>
          <Select
            menuPosition="fixed"
            className="b-mx-1"
            isSearchable={false}
            styles={selectStyles}
            noOptionsMessage={() => ""}
            value={
              component.attributes.delivery_method
                ? displays.find(
                    (x) => x.value === component.attributes.delivery_method
                  )
                : displays[1]
            }
            onChange={(val) => {
              if (!val?.value) return;
              updateStructureComponent(
                id,
                "attributes.delivery_method",
                val.value
              );
              triggerResize();
            }}
            options={displays}
            placeholder=""
          />
        </div>


        <div className="border-menu_input b-mx-1" style={{ padding: "0px" }}>
          <Select
            menuPosition="fixed"
            className="b-mx-1 "
            isSearchable={false}
            styles={selectStyles}
            noOptionsMessage={() => ""}
            value={
              deliverySources.find((x) => x.value === sourceSelectValue) ??
              deliverySources[0]
            }
            onChange={(val) => {
              if (!val?.value) return;
              handleSourceChange(val.value);
            }}
            options={deliverySources}
            placeholder=""
          />
        </div>

        {showFixedPicker && (
          <div
            className="content-width-menu_input position-relative"
            style={{ width: "200px" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                borderRadius: "4px",
                padding: "2px 4px",
                width: "100%",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuClicked(true);
                searchRef.current?.focus();
              }}
            >
              <input
                ref={searchRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                placeholder={fixedMethod ? "" : "Search delivery methods..."}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#fff",
                  width: "100%",
                  outline: "none",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              />
              {fixedMethod ? (
                <ClearIcon
                  style={{
                    fontSize: "16px",
                    marginLeft: "4px",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                  onClick={handleClearMethod}
                />
              ) : (
                <SearchIcon
                  style={{
                    fontSize: "16px",
                    marginLeft: "4px",
                    flexShrink: 0,
                  }}
                />
              )}
            </div>

            {isSearching && (
              <div
                style={{
                  position: "absolute",
                  width: "100%",
                  maxHeight: "200px",
                  overflowY: "auto",
                  top: "24px",
                  background: "#323232",
                  borderRadius: "2px",
                  zIndex: 1000,
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setIsMenuClicked(true);
                }}
              >
                {loading ? (
                  <div className="dropdown-item builder-dropdown__item">
                    Loading delivery methods...
                  </div>
                ) : error ? (
                  <div className="dropdown-item builder-dropdown__item">
                    {error}
                  </div>
                ) : (
                  <>
                    {[FIXED_NONE_OPTION, ...filteredMethods].map((method) => (
                      <div
                        key={method.uuid ?? "__none__"}
                        className={
                          "dropdown-item builder-dropdown__item" +
                          (fixedMethod?.uuid === method.uuid
                            ? " builder-dropdown__item--active"
                            : "")
                        }
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectMethod(method);
                        }}
                      >
                        {method.name}
                      </div>
                    ))}
                    {filteredMethods.length === 0 && (
                      <div className="dropdown-item builder-dropdown__item">
                        No delivery methods found
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div
        className="d-flex flex-wrap align-items-center"
        style={{ marginTop: "3px" }}
      >
        <div style={{ marginRight: "5px" }}>Payment method:</div>

        <div className="border-menu_input" style={{ padding: "0px" }}>
          <Select
            menuPosition="fixed"
            className="b-mx-1"
            isSearchable={false}
            styles={selectStyles}
            noOptionsMessage={() => ""}
            value={
              component.attributes.payment_method
                ? displays.find(
                    (x) => x.value === component.attributes.payment_method
                  )
                : displays[1]
            }
            onChange={(val) => {
              if (!val?.value) return;
              updateStructureComponent(
                id,
                "attributes.payment_method",
                val.value
              );
              triggerResize();
            }}
            options={displays}
            placeholder=""
          />
        </div>
      </div>

      <CheckoutPageUrlInput
        componentId={id}
        attributeKey="terms_url"
        label="Terms url:"
        savedUrl={component.attributes.terms_url}
        triggerResize={triggerResize}
      />
      <CheckoutPageUrlInput
        componentId={id}
        attributeKey="cookie_policy_url"
        label="Cookie policy url:"
        savedUrl={component.attributes.cookie_policy_url}
        triggerResize={triggerResize}
      />
      <CheckoutPageUrlInput
        componentId={id}
        attributeKey="privacy_policy_url"
        label="Privacy policy url:"
        savedUrl={component.attributes.privacy_policy_url}
        triggerResize={triggerResize}
      />

      <div className="b-my-1 d-flex align-items-center" style={{ gap: "8px" }}>
        <span>Confirm order button text:</span>
        <input
          type="text"
          value={component.attributes.confirm_order_button_text ?? ""}
          onChange={(e) => {
            updateStructureComponent(
              id,
              "attributes.confirm_order_button_text",
              e.target.value
            );
            triggerResize();
          }}
          placeholder="KINNITA TELLIMUS"
          style={{
            background: "#444",
            border: "0px solid #555",
            borderRadius: "3px",
            color: "#fff",
            height: "22px",
            paddingLeft: "5px",
            paddingRight: "5px",
            width: "200px",
          }}
        />
      </div>
    </div>
  );
}
